import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, CalendarClock, ExternalLink, History, Laptop, Link2, Pencil, ShieldAlert, Trash2, Undo2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ManagedDeviceCard } from '../components/ManagedDeviceCard';
import { AssetFormModal } from '../components/AssetFormModal';
import { ModelImage } from '../components/model-image';
import { RemoveRecordsDialog, useIsEdvoraStaff } from '../components/remove-records';
import { DeviceHistoryCard } from '../components/device-history';
import { AssetWorkOrdersCard } from '../components/help-desk-cards';
import { LocationHistoryCard } from '../components/location-history';
import { VerificationHistoryCard } from '../components/verification-history';
import { AssetStatusBadge, AueBadge, ConditionBadge, DeviceStatusBadge, DueBadge } from '../components/badges';
import { ChangeDueDateDialog, CheckInDialog, CheckOutDialog } from '../components/checkout-dialogs';
import { Combobox } from '../components/pickers';
import { useToast } from '../components/toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLoader,
  Table,
  Td,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ASSET_STATUS_LABEL,
  ASSIGNEE_TYPE_LABEL,
  formatDate,
  formatDateTime,
  formatMinutes,
  formatMoney,
  formatRelative,
} from '../lib/format';
import { invalidateInventory, useDebouncedValue } from '../lib/hooks';
import type { AssetDetail, Checkout, GoogleDeviceListItem, Page } from '../lib/types';

function assigneeLink(checkout: Pick<Checkout, 'personId' | 'roomId'>) {
  if (checkout.personId) return `/people/${checkout.personId}`;
  if (checkout.roomId) return `/locations/rooms/${checkout.roomId}`;
  return null;
}

function LinkDevicePicker({ assetId, serialNumber }: { assetId: string; serialNumber: string | null }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(serialNumber ?? '');
  const search = useDebouncedValue(query.trim(), 250);
  const results = useQuery({
    queryKey: ['google-devices', 'picker', search],
    queryFn: () => api.get<Page<GoogleDeviceListItem>>('/google-devices', { search, linked: 'false', pageSize: 8 }),
    enabled: search.length > 0,
  });
  const link = useMutation({
    mutationFn: (googleDeviceId: string) => api.put(`/assets/${assetId}/google-device`, { googleDeviceId }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Linked to Google device');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Combobox
      inline
      query={query}
      onQueryChange={setQuery}
      loading={results.isFetching || link.isPending}
      items={search ? (results.data?.items ?? []) : []}
      getKey={(d) => d.id}
      placeholder="Search unlinked devices by serial or user"
      emptyText="No unlinked devices match"
      onSelect={(device) => link.mutate(device.id)}
      renderItem={(device) => (
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold text-slate-900">{device.serialNumber}</p>
          <p className="truncate text-xs text-slate-500">{[device.model, device.mostRecentUser].filter(Boolean).join(' · ')}</p>
        </div>
      )}
    />
  );
}

export function AssetDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [changingDue, setChangingDue] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Edvora staff can take a device out with its history; a school retires it instead.
  const [removing, setRemoving] = useState(false);
  const edvora = useIsEdvoraStaff();
  const [linking, setLinking] = useState(false);

  const query = useQuery({ queryKey: ['asset', id], queryFn: () => api.get<AssetDetail>(`/assets/${id}`) });

  const unlink = useMutation({
    mutationFn: () => api.put(`/assets/${id}/google-device`, { googleDeviceId: null }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Unlinked Google device');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/assets/${id}`),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Asset deleted');
      navigate('/assets');
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const asset = query.data;
  const active = asset.activeCheckout;
  const canWrite = can('TECHNICIAN');
  const device = asset.googleDevice;
  const history = asset.checkouts.filter((c) => c.checkedInAt);
  const warrantyExpired = asset.warrantyExpiresAt && new Date(asset.warrantyExpiresAt).getTime() < Date.now();

  return (
    <>
      <PageHeader
        back={{ to: '/assets', label: 'Assets' }}
        image={asset.modelImageUrl ? <ModelImage url={asset.modelImageUrl} alt={asset.model ?? ''} className="size-20" /> : undefined}
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono">{asset.assetTag}</span>
            <span className="font-normal text-slate-400">·</span>
            <span>{asset.name}</span>
          </span>
        }
        meta={
          <>
            <AssetStatusBadge status={asset.status} />
            <ConditionBadge condition={asset.condition} />
            {asset.category && <Badge>{asset.category.name}</Badge>}
            {device && (
              <Badge tone="brand">
                <Laptop className="size-3" /> Google linked
              </Badge>
            )}
            {asset.managedDevice && <Badge tone="brand">MDM linked</Badge>}
            {asset.building?.kind === 'WAREHOUSE' && <Badge tone="violet">Off campus</Badge>}
          </>
        }
        actions={
          canWrite && (
            <>
              {can('ADMIN') && (
                <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              )}
              {edvora && (
                <Button variant="ghost" icon={<ShieldAlert />} onClick={() => setRemoving(true)}>
                  Remove (Edvora)
                </Button>
              )}
              <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit
              </Button>
              {asset.status === 'AVAILABLE' && (
                <Button variant="primary" icon={<ArrowLeftRight />} onClick={() => setCheckingOut(true)}>
                  Check out
                </Button>
              )}
              {active && (
                <Button variant="primary" icon={<Undo2 />} onClick={() => setCheckingIn(true)}>
                  Check in
                </Button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Current assignment" />
            <div className="p-5">
              {active ? (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">{ASSIGNEE_TYPE_LABEL[active.assigneeType]}</p>
                    {assigneeLink(active) ? (
                      <Link to={assigneeLink(active)!} className="text-lg font-semibold text-slate-900 hover:text-brand-700">
                        {active.assigneeLabel}
                      </Link>
                    ) : (
                      <p className="text-lg font-semibold text-slate-900">{active.assigneeLabel}</p>
                    )}
                    {active.person?.email && <p className="text-sm text-slate-500">{active.person.email}</p>}
                    <p className="text-sm text-slate-500">
                      Checked out {formatDate(active.checkedOutAt)}
                      {active.checkedOutBy && ` by ${active.checkedOutBy.name}`}
                    </p>
                    {active.notesOut && <p className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{active.notesOut}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <DueBadge dueAt={active.dueAt} />
                    {canWrite && (
                      <Button size="sm" variant="ghost" icon={<CalendarClock />} onClick={() => setChangingDue(true)}>
                        Change due date
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  {asset.status === 'AVAILABLE'
                    ? 'Not assigned - available to check out.'
                    : `Not assigned. This asset is marked ${ASSET_STATUS_LABEL[asset.status].toLowerCase()}.`}
                </p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Details" />
            <div className="p-5">
              <DescriptionList
                columns={3}
                items={[
                  { label: 'Manufacturer', value: asset.manufacturer },
                  {
                    label: 'Model',
                    value: asset.model && (
                      <>
                        {asset.model}
                        {asset.modelId && (
                          <Link to={`/parts?modelId=${asset.modelId}`} className="ml-2 text-xs font-medium text-brand-700 hover:underline">
                            Parts
                          </Link>
                        )}
                      </>
                    ),
                  },
                  { label: 'Serial number', value: asset.serialNumber && <span className="font-mono">{asset.serialNumber}</span> },
                  {
                    label: 'Home location',
                    value:
                      asset.room || asset.building || asset.cart ? (
                        <>
                          {asset.room ? (
                            <Link to={`/locations/rooms/${asset.room.id}`} className="text-brand-700 hover:underline">
                              {asset.room.name} · {asset.room.building.name}
                            </Link>
                          ) : (
                            asset.building?.name
                          )}
                          {asset.cart && (
                            <span className="block text-xs text-slate-500">
                              On{' '}
                              <Link to={`/carts/${asset.cart.id}`} className="text-brand-700 hover:underline">
                                {asset.cart.name}
                              </Link>
                              , which sets its building and room
                            </span>
                          )}
                        </>
                      ) : null,
                  },
                  { label: 'Purchase date', value: asset.purchaseDate && formatDate(asset.purchaseDate) },
                  { label: 'Purchase price', value: asset.purchasePrice && formatMoney(asset.purchasePrice) },
                  { label: 'Vendor', value: asset.vendor },
                  { label: 'PO number', value: asset.poNumber },
                  { label: 'Funding source', value: asset.fundingSource },
                  {
                    label: 'Warranty',
                    value: asset.warrantyExpiresAt && (
                      <span className={warrantyExpired ? 'text-red-700' : undefined}>
                        {warrantyExpired ? 'Expired ' : 'Until '}
                        {formatDate(asset.warrantyExpiresAt)}
                      </span>
                    ),
                  },
                  { label: 'Added', value: formatDate(asset.createdAt) },
                  { label: 'Retired', value: asset.retiredAt && formatDate(asset.retiredAt) },
                ]}
              />
              {asset.notes && (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">Notes</p>
                  <p className="mt-1 text-sm whitespace-pre-wrap text-slate-700">{asset.notes}</p>
                </div>
              )}
            </div>
          </Card>

          <LocationHistoryCard assetId={asset.id} />

          <VerificationHistoryCard assetId={asset.id} />

          {canWrite && <DeviceHistoryCard asset={asset} />}

          <Card>
            <CardHeader title="Checkout history" description={`${asset.checkouts.length} total`} />
            {history.length === 0 ? (
              <EmptyState icon={<History />} title="No past checkouts" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Assigned to</Th>
                    <Th>Checked out</Th>
                    <Th>Returned</Th>
                    <Th>Condition</Th>
                    <Th>Notes</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((checkout) => (
                    <tr key={checkout.id}>
                      <Td>
                        {assigneeLink(checkout) ? (
                          <Link to={assigneeLink(checkout)!} className="font-medium text-slate-900 hover:text-brand-700">
                            {checkout.assigneeLabel}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-900">{checkout.assigneeLabel}</span>
                        )}
                        <p className="text-xs text-slate-500">{ASSIGNEE_TYPE_LABEL[checkout.assigneeType]}</p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {formatDate(checkout.checkedOutAt)}
                        <p className="text-xs text-slate-500">{checkout.checkedOutBy?.name}</p>
                      </Td>
                      <Td className="whitespace-nowrap">
                        {formatDate(checkout.checkedInAt)}
                        <p className="text-xs text-slate-500">{checkout.checkedInBy?.name}</p>
                      </Td>
                      <Td className="text-xs whitespace-nowrap text-slate-600">
                        {checkout.conditionOut ?? '—'} → {checkout.conditionIn ?? '—'}
                      </Td>
                      <Td className="max-w-xs text-xs text-slate-600">{[checkout.notesOut, checkout.notesIn].filter(Boolean).join(' / ') || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <AssetWorkOrdersCard asset={asset} />

          {/* Chromebooks show their Google Admin record; everything else shows its MDM and Apple School Manager record. */}
          {(asset.managedDevice || (!device && !/chromebook/i.test(`${asset.category?.name ?? ''} ${asset.model ?? ''}`))) && <ManagedDeviceCard asset={asset} />}

          {(device || (!asset.managedDevice && /chromebook/i.test(`${asset.category?.name ?? ''} ${asset.model ?? ''}`))) && (
            <Card>
              <CardHeader
                title="Google ChromeOS device"
                actions={
                  device && (
                    <Link to={`/devices/${device.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800">
                      Open <ExternalLink className="size-3.5" />
                    </Link>
                  )
                }
              />
              <div className="p-5">
                {device ? (
                  <div className="space-y-4">
                    <DescriptionList
                      columns={1}
                      items={[
                        { label: 'Status', value: <DeviceStatusBadge status={device.status} /> },
                        { label: 'Last sync', value: device.lastSync && `${formatRelative(device.lastSync)} · ${formatDateTime(device.lastSync)}` },
                        { label: 'Most recent user', value: device.mostRecentUser },
                        { label: 'Active time', value: `${formatMinutes(device.activeMinutes7d)} this week · ${formatMinutes(device.activeMinutes30d)} in 30 days` },
                        { label: 'Auto-update expiration', value: <AueBadge date={device.autoUpdateExpiration} /> },
                        { label: 'ChromeOS', value: device.osVersion },
                        { label: 'Organizational unit', value: device.orgUnitPath },
                      ]}
                    />
                    {canWrite && (
                      <Button size="sm" variant="ghost" icon={<Unlink />} loading={unlink.isPending} onClick={() => unlink.mutate()}>
                        Unlink device
                      </Button>
                    )}
                  </div>
                ) : canWrite && linking ? (
                  <LinkDevicePicker assetId={asset.id} serialNumber={asset.serialNumber} />
                ) : (
                  <div className="text-sm text-slate-600">
                    <p>Not linked to a device in Google Admin.</p>
                    {canWrite && (
                      <Button size="sm" className="mt-3" icon={<Link2 />} onClick={() => setLinking(true)}>
                        Link a device
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Activity" />
            {asset.history.length === 0 ? (
              <EmptyState title="No activity recorded" />
            ) : (
              <ol className="space-y-4 p-5">
                {asset.history.map((entry) => (
                  <li key={entry.id} className="relative pl-5 text-sm">
                    <span className="absolute top-1.5 left-0 size-2 rounded-full bg-brand-400" />
                    <p className="text-slate-800">{entry.summary}</p>
                    <p className="text-xs text-slate-500">
                      {entry.actorName ?? 'System'} · {formatDateTime(entry.createdAt)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      <AssetFormModal open={editing} onClose={() => setEditing(false)} asset={asset} />
      <CheckOutDialog open={checkingOut} onClose={() => setCheckingOut(false)} assets={[asset]} />
      {active && (
        <>
          <CheckInDialog open={checkingIn} onClose={() => setCheckingIn(false)} checkout={active} />
          <ChangeDueDateDialog open={changingDue} onClose={() => setChangingDue(false)} checkout={active} />
        </>
      )}
      <RemoveRecordsDialog open={removing} onClose={() => setRemoving(false)} subjects={{ assetIds: [asset.id] }} onRemoved={() => navigate('/assets')} />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete ${asset.assetTag}?`}
        description="This permanently removes the asset. Assets with checkout history can’t be deleted - retire them instead."
        confirmLabel="Delete asset"
      />
    </>
  );
}
